import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Handshake, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { soldText } from "@/lib/proacText";

const DealCard = ({ d, i }) => (
  <motion.article
    data-testid={`sold-deal-${d.deal_id}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
    className="bg-white border border-gray-200 flex flex-col overflow-hidden group"
  >
    <div className="relative aspect-[3/2] overflow-hidden">
      <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <span className="absolute top-3 left-3 bg-[#1B2A4A] text-white text-[11px] uppercase tracking-wider px-3 py-1 flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-[#E0C173]" strokeWidth={2} /> Cession réalisée
      </span>
      {d.reference && <span className="absolute top-3 right-3 bg-white/90 text-[#1B2A4A] text-[11px] uppercase tracking-wider px-3 py-1">Réf. {d.reference}</span>}
    </div>
    <div className="p-6 flex flex-col gap-3 flex-1">
      <span className="text-xs uppercase tracking-widest text-[#C9A85A]">{d.sector}</span>
      <h3 className="font-display text-xl font-semibold text-[#1B2A4A] leading-snug">{d.headline}</h3>
      <div className="flex items-center gap-1.5 text-sm text-[#475569]">
        <MapPin className="w-4 h-4 text-[#E0C173]" strokeWidth={1.5} />
        {d.region}{d.department && ` (${d.department})`}
      </div>
      <p className="text-sm text-[#475569] leading-relaxed">{soldText(d.name, d.description)}</p>
      {d.buyer && (
        <div className="mt-auto pt-4 border-t border-gray-100 flex items-center gap-2 text-sm text-[#1B2A4A]">
          <Handshake className="w-4 h-4 text-[#E0C173] shrink-0" strokeWidth={1.5} /> <span data-testid={`sold-deal-buyer-${d.deal_id}`}>Repreneur : <strong>{d.buyer}</strong></span>
        </div>
      )}
    </div>
  </motion.article>
);

export default function SoldDeals() {
  const [deals, setDeals] = useState([]);
  useEffect(() => { api.get("/sold-deals").then((r) => setDeals(r.data)); }, []);

  return (
    <div className="bg-[#FDFDFC]">
      <Header />
      <section className="bg-[#1B2A4A] py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Nos références</span>
          <h1 data-testid="sold-deals-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mt-4 max-w-3xl">Nos dernières cessions</h1>
          <p className="text-white/75 text-base md:text-lg mt-5 max-w-2xl">Des transmissions menées à bien par PROAC Transmission, au service des cédants comme des repreneurs.</p>
          <div data-testid="sold-deals-count" className="mt-8 inline-flex items-baseline gap-2 text-white">
            <span className="font-display text-4xl font-semibold text-[#E0C173]">{deals.length}</span>
            <span className="text-sm uppercase tracking-widest text-white/70">cessions présentées</span>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
        <div data-testid="sold-deals-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {deals.map((d, i) => <DealCard key={d.name} d={d} i={i} />)}
        </div>
      </section>
      <Footer />
    </div>
  );
}
